"use client";

import { useState } from "react";

import { QuestionOption } from "@/components/quiz/question-option";
import {
  INCOME_BUCKETS,
  INCOME_QUESTION_HELPER,
  INCOME_QUESTION_TEXT,
  PARTNER_INCOME_CHECKBOX_LABEL,
  PARTNER_INCOME_SELECT_LABEL,
  type IncomeBucket,
} from "@/lib/quiz/income-question";

/**
 * F1 (Sprint 16) — the non-scored mid-quiz income card.
 *
 * Renders inside the existing quiz chrome (the parent wraps it in QuizShell).
 * The user picks one household bucket; an optional checkbox reveals a second
 * bucket selector for a partner's income. "Continue" is enabled once the
 * household bucket is chosen — the partner bucket is required only when the
 * box is checked, so a checked-but-unanswered partner can't be submitted.
 *
 * This card never auto-advances (unlike the scored questions): income has two
 * inputs, so the user taps "Continue" explicitly. The parent owns persistence
 * and analytics; this component only collects and hands back the two buckets.
 */

interface IncomeQuestionCardProps {
  onSubmit: (income: IncomeBucket, partner: IncomeBucket | null) => void;
  reduceMotion: boolean;
}

export function IncomeQuestionCard({
  onSubmit,
  reduceMotion,
}: IncomeQuestionCardProps) {
  const [income, setIncome] = useState<IncomeBucket | null>(null);
  const [includePartner, setIncludePartner] = useState(false);
  const [partner, setPartner] = useState<IncomeBucket | null>(null);

  const canContinue = income !== null && (!includePartner || partner !== null);

  return (
    <div
      className={`mx-auto mt-6 max-w-column ${
        reduceMotion ? "" : "animate-slide-in-question"
      }`}
    >
      <div className="quiz-card p-5 sm:p-7" data-income-card="true">
        <h1 className="mt-1 text-lead text-ink md:text-lead-lg">
          {INCOME_QUESTION_TEXT}
        </h1>
        <p className="mt-2 max-w-prose text-sub text-mute">
          {INCOME_QUESTION_HELPER}
        </p>

        <ul className="mt-6 space-y-2.5" aria-label="Your monthly take-home pay">
          {INCOME_BUCKETS.map((bucket, i) => {
            const selected = income === bucket;
            return (
              <li key={bucket}>
                <QuestionOption
                  numeral={String(i + 1)}
                  label={bucket}
                  onSelect={() => setIncome(bucket)}
                  selected={selected}
                  dataAttrs={{
                    "data-income-bucket": bucket,
                    "data-selected": selected ? "true" : "false",
                  }}
                  trailing={
                    selected ? (
                      <span
                        aria-hidden="true"
                        className="text-caption font-semibold text-teal"
                      >
                        {reduceMotion ? "selected" : "✓"}
                      </span>
                    ) : undefined
                  }
                />
              </li>
            );
          })}
        </ul>

        <label className="mt-6 flex cursor-pointer items-center gap-3 text-sub text-ink">
          <input
            type="checkbox"
            checked={includePartner}
            onChange={(e) => {
              const checked = e.target.checked;
              setIncludePartner(checked);
              if (!checked) setPartner(null);
            }}
            className="h-4 w-4 accent-teal"
            data-partner-toggle="true"
          />
          {PARTNER_INCOME_CHECKBOX_LABEL}
        </label>

        {includePartner ? (
          <div className="mt-5" data-partner-section="true">
            <p className="text-caption font-semibold text-mute">
              {PARTNER_INCOME_SELECT_LABEL}
            </p>
            <ul
              className="mt-2.5 space-y-2.5"
              aria-label="Your partner's monthly take-home pay"
            >
              {INCOME_BUCKETS.map((bucket, i) => {
                const selected = partner === bucket;
                return (
                  <li key={bucket}>
                    <QuestionOption
                      numeral={String(i + 1)}
                      label={bucket}
                      onSelect={() => setPartner(bucket)}
                      selected={selected}
                      dataAttrs={{
                        "data-partner-bucket": bucket,
                        "data-selected": selected ? "true" : "false",
                      }}
                      trailing={
                        selected ? (
                          <span
                            aria-hidden="true"
                            className="text-caption font-semibold text-teal"
                          >
                            {reduceMotion ? "selected" : "✓"}
                          </span>
                        ) : undefined
                      }
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

        <div className="mt-6">
          <button
            type="button"
            disabled={!canContinue}
            onClick={() => {
              if (!canContinue || income === null) return;
              onSubmit(income, includePartner ? partner : null);
            }}
            className="btn-primary disabled:cursor-not-allowed disabled:opacity-60"
            data-income-continue="true"
          >
            Continue
            <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    </div>
  );
}
